import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Image,
  Divider,
  Select,
  IconButton,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';

import PlusIcon from '../../../assets/icons/plus.svg';
import {
  useGetAllProjectsQuery,
  useGetMyTaskInsightsQuery,
  useGetTeamTaskInsightsQuery,
} from '../../../services';
import { CenteredSpinner } from '../../../components';
import { useTypedSelector } from '../../../hooks';
import { Tasks } from '../../tasks';
import TotalFromIcon from '../../../assets/home/total-forms.svg';
import LateFromIcon from '../../../assets/home/late-form.svg';
import DueDateFromIcon from '../../../assets/home/due-date-from.svg';
import { ReactComponent as MyTasksIcon } from '../../../assets/tasks/my-tasks.svg';
import { ReactComponent as MyTasksSelectedIcon } from '../../../assets/tasks/my-tasks-selected.svg';
import { ReactComponent as TeamTasksIcon } from '../../../assets/tasks/team-tasks.svg';
import { ReactComponent as TeamTasksSelectedIcon } from '../../../assets/tasks/team-tasks-selected.svg';
import { trimString } from '../../../utils';
import { Can } from '../../../providers';
import { TaskInsights } from '../../../models';

export const Home: FC = () => {
  const [selectedProjectId, setSelectedProjectId] = useState<number>();
  const [isNewTaskBtnClicked, setIsNewTaskBtnClicked] = useState(0);
  const [taskInsights, setTaskInsights] = useState<TaskInsights>();
  const { userDetails } = useTypedSelector(state => state.auth);

  const { data: allProjects } = useGetAllProjectsQuery({
    page: 0,
    size: 1000,
  });

  const isTeamTasks = location.pathname.includes('my-team');

  const { data: myTaskInsights, isLoading: isMyTaskInsightsLoading } =
    useGetMyTaskInsightsQuery(
      {
        projectId: selectedProjectId,
      },
      {
        skip: isTeamTasks,
      }
    );

  const { data: teamTaskInsights, isLoading: isTeamTaskInsightsLoading } =
    useGetTeamTaskInsightsQuery(
      {
        projectId: selectedProjectId,
      },
      {
        skip: !selectedProjectId || !isTeamTasks,
      }
    );

  const navigate = useNavigate();

  useEffect(() => {
    if (myTaskInsights?.data || teamTaskInsights?.data) {
      setTaskInsights(myTaskInsights?.data || teamTaskInsights?.data);
    }
  }, [myTaskInsights?.data, teamTaskInsights?.data]);

  if (!userDetails) {
    return (
      <VStack h="100vh">
        <CenteredSpinner />
      </VStack>
    );
  }

  return (
    <VStack align="stretch" mx="8" pb="2">
      <HStack justify="space-between" mt="2" mb="3">
        <Box fontSize="3xl" fontWeight="semibold" color="custom_blue.1">
          Welcome back, {userDetails.firstName}
        </Box>

        <HStack>
          <HStack>
            <Can I="POST" a="company/taskBuilding/task">
              <Button
                onClick={() => setIsNewTaskBtnClicked(prev => prev + 1)}
                bg="custom_orange.2"
                _hover={{ bg: 'custom_orange.2' }}
                color="white"
                fontWeight="semibold"
                fontSize="sm"
                mr="4"
              >
                <Image src={PlusIcon} alt="plus icon" boxSize="5" mr="1" />
                Start Task
              </Button>
            </Can>

            <Can I="GET" a="company/taskBuilding/teamTaskList">
              <HStack>
                <IconButton
                  bg={!isTeamTasks ? 'custom_red.3' : 'white'}
                  _hover={{ bg: !isTeamTasks ? 'custom_red.3' : 'white' }}
                  icon={
                    !isTeamTasks ? <MyTasksSelectedIcon /> : <MyTasksIcon />
                  }
                  onClick={() => navigate('/')}
                  aria-label={''}
                />

                <IconButton
                  bg={isTeamTasks ? 'custom_red.3' : 'white'}
                  _hover={{ bg: !isTeamTasks ? 'custom_red.3' : 'white' }}
                  icon={
                    !isTeamTasks ? <TeamTasksIcon /> : <TeamTasksSelectedIcon />
                  }
                  onClick={() => navigate('/my-team')}
                  aria-label={''}
                />
              </HStack>
            </Can>
          </HStack>
        </HStack>
      </HStack>

      <VStack
        align="stretch"
        bg="white"
        borderRadius="md"
        p="4"
        border="1px"
        borderColor="custom_gray.3"
        minH="80"
      >
        <Select
          variant="unstyled"
          placeholder="Project Name"
          onChange={e => setSelectedProjectId(Number(e.target.value))}
          w="fit-content"
          fontSize="lg"
          fontWeight="medium"
          color="custom_blue.1"
        >
          {allProjects?.data.content.map(({ id, projectName }) => (
            <option key={id} value={id}>
              {projectName}
            </option>
          ))}
        </Select>
        {isMyTaskInsightsLoading || isTeamTaskInsightsLoading ? (
          <CenteredSpinner />
        ) : (
          <Box>
            <HStack gap="4" mt="4">
              <HStack flex="1" justify="space-between">
                <HStack>
                  <Box bg="#16DBCC14" p="3" borderRadius="md">
                    <Image src={TotalFromIcon} />
                  </Box>

                  <VStack flex="1">
                    <Box fontSize="sm" color="custom_gray.9">
                      Total Forums
                    </Box>
                    <Box fontSize="xl" fontWeight="semibold" color="#232323">
                      {taskInsights?.total}
                    </Box>
                  </VStack>
                </HStack>

                <Divider orientation="vertical" h="12" />
              </HStack>

              <HStack flex="1" justify="space-between">
                <HStack>
                  <Box bg="#FFEAEA" p="3" borderRadius="md">
                    <Image src={LateFromIcon} />
                  </Box>

                  <VStack flex="1">
                    <Box fontSize="sm" color="custom_gray.9">
                      Late Forums
                    </Box>
                    <Box fontSize="xl" fontWeight="semibold" color="#232323">
                      {taskInsights?.late}
                    </Box>
                  </VStack>
                </HStack>

                <Divider orientation="vertical" h="12" />
              </HStack>

              <HStack flex="1" justify="space-between">
                <HStack>
                  <Box bg="#FEDB6326" p="3" borderRadius="md">
                    <Image src={DueDateFromIcon} />
                  </Box>

                  <VStack flex="1">
                    <Box fontSize="sm" color="custom_gray.9">
                      Due Today Forums
                    </Box>
                    <Box fontSize="xl" fontWeight="semibold" color="#232323">
                      {taskInsights?.dueToday}
                    </Box>
                  </VStack>
                </HStack>
              </HStack>
            </HStack>
            <Divider mt="2" />
            <Box fontSize="lg" fontWeight="medium" color="custom_blue.1" mt="3">
              Forums Closed This Month
            </Box>
            <Box h="72" mt="4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  width={500}
                  height={300}
                  data={taskInsights?.taskInsightsMonthlyResponse
                    .slice(0, 10)
                    .map(({ type, completed, total }) => ({
                      name: trimString(type),
                      completed,
                      total,
                    }))}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 25,
                  }}
                  barSize={20}
                >
                  <XAxis
                    dataKey="name"
                    label={{
                      value: new Date().toLocaleDateString('en-US', {
                        month: 'long',
                      }),
                      offset: -20,
                      position: 'insideBottom',
                    }}
                  />
                  <YAxis
                    label={{
                      value: 'Forums',
                      angle: -90,
                      position: 'left',
                    }}
                  />
                  <CartesianGrid strokeDasharray="3 3" />
                  <Bar
                    dataKey="completed"
                    name="completed"
                    stackId="a"
                    fill="#ec5e2a"
                  />
                  <Bar
                    dataKey="total"
                    stackId="a"
                    name="total"
                    fill="#EAECF0"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}
      </VStack>

      <Box>
        <Tasks
          isRenderedAtHome={true}
          isNewTaskFromHome={isNewTaskBtnClicked}
          selectedProjectId={selectedProjectId}
        />
      </Box>
    </VStack>
  );
};
